// frontend/src/pages/GlobalSearchPage.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlass, X } from "phosphor-react";
import { api } from "api";
import BackButton from "../components/BackButton";
import { DEFAULT_CIRCLE_IMAGE, DEFAULT_PROFILE_IMAGE } from "../utils/blank-image";

export default function GlobalSearchPage() {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [tags, setTags] = useState([]);
  const [error, setError] = useState("");
  const inputRef = useRef(null);
  const nav = useNavigate();

  const clearSearch = () => {
    setQ("");
    setUsers([]);
    setGroups([]);
    setTags([]);
    setError("");
    setLoading(false);
  };

  const debouncedQ = useMemo(() => q.trim(), [q]);
  const isTagMode = debouncedQ.startsWith("#");
  const tagPrefix = useMemo(
    () => (isTagMode ? debouncedQ.replace(/^#/, "").trim().toLowerCase() : ""),
    [debouncedQ, isTagMode]
  );

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        clearSearch();
        inputRef.current?.focus();
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!debouncedQ) {
      setUsers([]);
      setGroups([]);
      setTags([]);
      setLoading(false);
      setError("");
      return;
    }

    if (isTagMode) {
      setUsers([]);
      setGroups([]);
      setError("");

      if (!tagPrefix) {
        setTags([]);
        setLoading(false);
        return;
      }

      let cancelled = false;

      (async () => {
        setLoading(true);

        try {
          const token = localStorage.getItem("token");
          const headers = token ? { Authorization: `Bearer ${token}` } : {};

          const tryEndpoints = [
            { url: `/api/tags/search`, params: { prefix: tagPrefix } },

            // fallbacks
            { url: `/activities/tags/search`, params: { prefix: tagPrefix } },
            { url: `/tags/search`, params: { prefix: tagPrefix } },
            { url: `/tags`, params: { prefix: tagPrefix } },
            { url: `/api/tags`, params: { prefix: tagPrefix } },
            { url: `/tags/popular`, params: {} },
          ];

          let found = null;

          for (const ep of tryEndpoints) {
            try {
              const res = await api.get(ep.url, { params: ep.params, headers });
              const raw = res?.data;
              if (!raw) continue;

              let list = Array.isArray(raw) ? raw : raw?.items;
              if (!Array.isArray(list)) continue;

              if (ep.url.includes("/popular")) {
                list = list.filter((t) => {
                  const n = (t?.name ?? t)?.toString().toLowerCase();
                  return n.startsWith(tagPrefix);
                });
              }

              const normalized = list
                .map((t) => {
                  const name = (t?.name ?? t)?.toString().toLowerCase().trim();
                  return name ? { name } : null;
                })
                .filter(Boolean);

              const prefixOnly = normalized.filter((t) =>
                t.name.startsWith(tagPrefix)
              );

              const uniq = Array.from(
                new Set(prefixOnly.map((t) => t.name))
              ).map((name) => ({ name }));

              found = uniq;
              break;
            } catch {
              // try next endpoint
            }
          }

          if (!cancelled) {
            setTags(Array.isArray(found) ? found.slice(0, 10) : []);
          }
        } catch (e) {
          if (!cancelled) {
            console.error("tag search failed", e?.response?.data || e);
            setTags([]);
            setError("Search failed");
          }
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();

      return () => {
        cancelled = true;
      };
    }

    let cancelled = false;

    (async () => {
      setLoading(true);
      setError("");

      try {
        const token = localStorage.getItem("token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        const [uRes, gRes] = await Promise.all([
          api.get(`/users/search`, { params: { q: debouncedQ }, headers }),
          api.get(`/groups/search`, { params: { q: debouncedQ }, headers }),
        ]);

        if (cancelled) return;

        setUsers(Array.isArray(uRes.data) ? uRes.data : []);
        setGroups(Array.isArray(gRes.data) ? gRes.data : []);
        setTags([]);
      } catch (err) {
        if (cancelled) return;
        console.error("search failed", err?.response?.data || err);
        setError("Search failed");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [debouncedQ, isTagMode, tagPrefix]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!debouncedQ) return;

    if (isTagMode && tagPrefix) {
      nav(`/tags/${encodeURIComponent(tagPrefix)}`);
    }
  };

  const ResultRow = ({ to, avatar, title, subtitle, fallback }) => (
    <button
      type="button"
      onClick={() => nav(to)}
      className="flex items-center gap-3 -ml-1.5 pl-1.5 pr-0.5 !min-h-0 !min-w-0 !py-1.5 hover:bg-gray-50 rounded-3xl text-left"
    >
      <img
        src={avatar || fallback}
        alt=""
        className="w-8 h-8 rounded-full object-cover"
      />
      <div className="min-w-0">
        <div className="font-medium truncate">{title}</div>
        {subtitle && (
          <div className="text-xs text-gray-500 truncate">{subtitle}</div>
        )}
      </div>
    </button>
  );

  const TagRow = ({ name }) => (
    <button
      type="button"
      onClick={() => nav(`/tags/${encodeURIComponent(name)}`)}
      className="inline-flex items-center gap-3 !min-w-0 !min-h-0 !px-3 !py-1.5 !h-8 rounded-3xl text-left"
    >
      <div className="min-w-0">
        <div className="font-medium truncate">#{name}</div>
      </div>
    </button>
  );

  return (
    <div className="app-shell bg-blue-50">
      <header
        className="sticky top-0 left-0 right-0 z-30 bg-blue-50"
        style={{ top: "var(--safe-top)" }}
      >
        <div className="mt-2 mx-auto px-4 flex items-center">
          <BackButton className="text-blue-600" ariaLabel="Back" />
          <h1 className="text-[20px] text-blue-600 dm-font font-black tracking-[-0.06em] leading-[0.9] select-none">
            Search
          </h1>
          <div className="w-7" />
        </div>
      </header>

      <main
        className="app-content no-scrollbar w-full px-6 pt-2 space-y-3"
      >
        <div className="bg-white rounded-3xl px-4 py-0 sm:py-3 min-h-10 flex items-center gap-3">
          <MagnifyingGlass size={20} className="text-gray-500" />
          <form onSubmit={handleSubmit} className="flex-1">
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search members, circles, or #tags"
              className="w-full outline-none"
              inputMode="search"
              enterKeyHint="search"
              aria-label="Global search"
            />
          </form>

          {q && (
            <button
              type="button"
              className="p-1 rounded hover:bg-gray-100"
              onClick={() => {
                clearSearch();
                inputRef.current?.focus();
              }}
              aria-label="Clear search"
            >
              <X size={18} />
            </button>
          )}
        </div>

        {debouncedQ !== "" && (
          <div className="bg-white rounded-3xl shadow-sm p-4">
            {loading && (
              <div className="p-3 text-sm text-gray-500">Searching…</div>
            )}

            {error && <div className="p-3 text-sm text-red-600">{error}</div>}

            {isTagMode && (
              <div>
                <div className="text-xs font-semibold text-blue-800 px-1 mb-1">
                  Tags
                </div>

                <div className="space-y-1">
                  {tags.length > 0 ? (
                    tags
                      .slice(0, 10)
                      .map((t) => <TagRow key={t.name} name={t.name} />)
                  ) : (
                    !loading && (
                      <div className="px-1.5 py-1 text-xs text-gray-400">
                        {tagPrefix
                          ? `No tags starting with “#${tagPrefix}”`
                          : "Type a tag - e.g. #piano"}
                      </div>
                    )
                  )}
                </div>
              </div>
            )}

            {!isTagMode &&
              !loading &&
              !error &&
              (users.length > 0 || groups.length > 0) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs font-semibold text-blue-800 px-1 mb-1">
                      Members
                    </div>

                    <div className="space-y-0">
                      {users.slice(0, 6).map((u) => (
                        <ResultRow
                          key={`u-${u.id}`}
                          to={`/profile/${u.id}`}
                          avatar={u.profile_pic_url}
                          title={u.name || u.username || `User #${u.id}`}
                          subtitle={u.email}
                          fallback={DEFAULT_PROFILE_IMAGE}
                        />
                      ))}

                      {users.length === 0 && (
                        <div className="px-3 py-2 text-sm text-gray-400">
                          No members found
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs font-semibold text-blue-800 px-1 mb-1">
                      Circles
                    </div>

                    <div className="space-y-0">
                      {groups.slice(0, 6).map((g) => (
                        <ResultRow
                          key={`g-${g.id}`}
                          to={`/circles/${g.id}`}
                          avatar={g.image_url || g.icon_url}
                          title={g.name || `Circle #${g.id}`}
                          subtitle={`${g.member_count ?? ""} members`}
                          fallback={DEFAULT_CIRCLE_IMAGE}
                        />
                      ))}

                      {groups.length === 0 && (
                        <div className="px-3 py-2 text-sm text-gray-400">
                          No circles found
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

            {!isTagMode &&
              !loading &&
              !error &&
              q &&
              users.length === 0 &&
              groups.length === 0 && (
                <div className="p-3 text-sm text-gray-400">
                  No results for “{q}”
                </div>
              )}
          </div>
        )}

        <div className="app-footer-pad" />
      </main>
    </div>
  );
}